/**autor:娄抗原
 * table
 *    columns 设置width 格式为number类型是可以拖拽列 如width:120
 *    searchType
 *      不输入就没有表头搜索
 *      time 日期选择器 YYYY-MM-DD HH:mm:ss 格式
 *      date 日期选择器 YYYY-MM-DD 格式
 *      input 输入框
 *      radio 选择框单选
 *      multiple 选择框单选
 *    pagination 默认展示分页器 可自定义
 *    setSelectedRowKeys 多选记录id
 *    selectedRowKeys 判断选中没有
 */
import React, { SetStateAction, memo, useEffect, useMemo, useReducer } from 'react'
import useAntdResizableHeader from 'use-antd-resizable-header'
import '@minko-fe/use-antd-resizable-header/index.css'
import { DatePicker, Form, Input, Select, Table, Tooltip } from 'antd'
import styled from 'styled-components'
import { UpOutlined, DownOutlined } from '@ant-design/icons'
import { IconFont } from '../iconfont'
import { ColumnsType } from 'antd/lib/table'
import { BatchOperation } from '../batch-operation'
import { useDebounceFn } from 'ahooks'
import { OptionType } from '../filter-form'
import { ShowTotal } from '../show-total'
import dayjs from '@/utils/dayjs'
import { SvgIcon } from '../svg-icon'
import { t } from '@/i18n'
export type ColumnsProps = ColumnsType<object> &
  {
    searchType?: 'input' | 'multiple' | 'radio' | 'time' | 'date' | 'dateNoTime'
    data?: { id: string; name: string }[]
  }[]
type Params = {
  page: number
  limit: number
  [k: string]: any
}
interface DraggingTableHeadersProps extends React.ComponentProps<typeof Table> {
  isScript?: boolean
  isNested?: boolean
  columns: ColumnsProps
  expandedRowKeys: string[]
  expandedRowRender?: (record: any) => React.ReactNode
  onExpand: (expanded: boolean, record: any) => void
  dataSource: any[]
  searchParams?: Params | any
  setSearchParams: React.Dispatch<React.SetStateAction<Params | any>>
  total?: number
  loading?: boolean
  selectedRowKeys: string[]
  selectedSubRowKeys: string[]
  setSelectedRowKeys: React.Dispatch<SetStateAction<string[]>>
  setSelectedSubRowKeys: React.Dispatch<SetStateAction<string[]>>
  /**批量操作展示 */
  batchOperation?: React.ReactNode
  /**表头筛选*/
  option?: OptionType
  /**列表和搜索同步搜索参数 仅限在list-page使用 */
  setFilterParams?: React.Dispatch<
    React.SetStateAction<{ [k: string]: string | number | undefined } | undefined>
  >

  filterParams?:
    | {
        [k: string]: string | number | undefined
      }
    | undefined
  /**如果有需要额外配置rowSelection的，使用rowSelections参数 */
  rowSelections?: object
}
export const DraggingTableHeaders: React.FC<DraggingTableHeadersProps> = memo(
  ({
    isScript,
    isNested,
    columns,
    dataSource,
    expandedRowKeys,
    expandedRowRender,
    onExpand,
    total = 0,
    searchParams,
    setSearchParams,
    selectedRowKeys,
    selectedSubRowKeys,
    batchOperation,
    setSelectedRowKeys,
    setSelectedSubRowKeys,
    option,
    loading = false,
    setFilterParams,
    filterParams,
    ...other
  }) => {
    const [form] = Form.useForm()
    const { run } = useDebounceFn(
      d => {
        let obj = JSON.parse(JSON.stringify(d))
        let obj2 = {}
        for (const key in option) {
          if (option[key]?.type === 'time') {
            if ((option[key]?.apiParams || [])?.length > 0) {
              obj2[(option[key]?.apiParams || [])?.[0]] =
                obj[key]?.length > 0 ? dayjs(obj[key]?.[0]).format('YYYY-MM-DD HH:mm:ss') : ''
              obj2[(option[key]?.apiParams || [])?.[1]] =
                obj[key]?.length > 0 ? dayjs(obj[key]?.[1]).format('YYYY-MM-DD HH:mm:ss') : ''
            }
            obj[key] = undefined
          }
          if (option[key]?.type === 'date') {
            if ((option[key]?.apiParams || [])?.length > 0) {
              obj2[(option[key]?.apiParams || [])?.[0]] =
                obj[key]?.length > 0 ? `${dayjs(obj[key]?.[0]).format('YYYY-MM-DD')} 00:00:00` : ''
              obj2[(option[key]?.apiParams || [])?.[1]] =
                obj[key]?.length > 0 ? `${dayjs(obj[key]?.[1]).format('YYYY-MM-DD')} 23:59:59` : ''
            }
            obj[key] = undefined
          }
          if (option[key]?.type === 'dateNoTime') {
            if ((option[key]?.apiParams || [])?.length > 0) {
              obj2[(option[key]?.apiParams || [])?.[0]] =
                obj[key]?.length > 0 ? dayjs(obj[key]?.[0]).format('YYYY-MM-DD') : ''
              obj2[(option[key]?.apiParams || [])?.[1]] =
                obj[key]?.length > 0 ? dayjs(obj[key]?.[1]).format('YYYY-MM-DD') : ''
            }
            obj[key] = undefined
          }
          if (option[key]?.type === 'multiple') {
            if (option[key]?.typeof && option[key]?.typeof === 'array') {
              obj[key] = obj[key] || undefined
            } else obj[key] = obj[key]?.join(',') || undefined
          }
          if (option[key]?.type === 'radio') {
            obj[key] = obj[key] || undefined
          }
        }
        if (setFilterParams) setFilterParams(d)
        setSearchParams((origin: any) => ({ ...origin, ...obj, ...obj2, page: 1 }))
      },
      {
        wait: 500,
      },
    )
    useEffect(() => {
      if (filterParams) {
        if (Object.keys(filterParams)?.length > 0) {
          form.setFieldsValue(filterParams)
        } else {
          form.resetFields()
        }
      }
    }, [filterParams])
    const [r, forceRender] = useReducer(s => s + 1, 0)
    const getColumnSearchProps = (dataIndex, searchType) => ({
      filterDropdown: ({ setSelectedKeys, selectedKeys }) => (
        <div style={{ padding: 8 }} onKeyDown={e => e.stopPropagation()}>
          <Form
            form={form}
            onValuesChange={(e, d) => {
              run(d)
            }}
          >
            <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
              {searchType !== 'date' && searchType !== 'time' && searchType !== 'dateNoTime' && (
                <div style={{ width: 280 }}>
                  {searchType === 'input' && (
                    <Form.Item style={{ margin: 0 }} name={dataIndex}>
                      <Input
                        placeholder={t('请输入')}
                        value={selectedKeys[0]}
                        onChange={e => setSelectedKeys(e.target.value ? [e.target.value] : [])}
                      />
                    </Form.Item>
                  )}
                  {(searchType === 'radio' || searchType === 'multiple') && (
                    <Form.Item style={{ margin: 0 }} name={dataIndex}>
                      <Select
                        allowClear
                        mode={searchType}
                        maxTagTextLength={4}
                        maxTagCount={2}
                        showArrow
                        placeholder={t('请选择')}
                        showSearch
                        optionFilterProp={'label'}
                      >
                        {option?.[dataIndex]?.data?.map(item => (
                          <Select.Option key={item.id} value={item?.id} label={item.name}>
                            {item?.name}
                          </Select.Option>
                        ))}
                      </Select>
                    </Form.Item>
                  )}
                </div>
              )}
              {searchType === 'time' && (
                <div style={{ width: 360 }}>
                  <Form.Item style={{ margin: 0 }} name={dataIndex}>
                    <DatePicker.RangePicker
                      showTime
                      placeholder={[t('请选择日期'), t('请选择日期')]}
                    />
                  </Form.Item>
                </div>
              )}
              {searchType === 'date' && (
                <div style={{ width: 260 }}>
                  <Form.Item style={{ margin: 0 }} name={dataIndex}>
                    <DatePicker.RangePicker placeholder={[t('请选择日期'), t('请选择日期')]} />
                  </Form.Item>
                </div>
              )}
              {searchType === 'dateNoTime' && (
                <div style={{ width: 260 }}>
                  <Form.Item style={{ margin: 0 }} name={dataIndex}>
                    <DatePicker.RangePicker placeholder={[t('请选择日期'), t('请选择日期')]} />
                  </Form.Item>
                </div>
              )}
              <Reset
                onClick={() => {
                  if (setFilterParams) {
                    setFilterParams(
                      origin => ({ ...origin, [dataIndex]: undefined, page: 1 } as any),
                    )
                    if (
                      searchType !== 'time' &&
                      searchType !== 'date' &&
                      searchType !== 'dateNoTime'
                    )
                      setSearchParams(origin => ({ ...origin, [dataIndex]: undefined, page: 1 }))
                  }
                  if (searchType === 'time' || searchType === 'date' || searchType === 'dateNoTime')
                    run({ [dataIndex]: [] })
                  form.setFieldValue(dataIndex, undefined)
                }}
              >
                {t('重置')}
              </Reset>
            </div>
          </Form>
        </div>
      ),

      filterIcon: () => (
        <Tooltip title={t('筛选')} getPopupContainer={() => document.body}>
          <Icon type={'icon-shaixuan1'} />
        </Tooltip>
      ),
    })
    const { components, resizableColumns, tableWidth } = useAntdResizableHeader({
      columns: useMemo(() => {
        let arr = columns.slice(0) as any
        arr.map((item, index) => {
          if (item?.searchType) {
            arr[index] = {
              ...arr[index],
              ...getColumnSearchProps(arr[index]?.dataIndex, arr[index]?.searchType),
            }
          }
        })
        return arr
      }, [r]),
      minConstraints: 120, //拖动最小宽度
    })

    const onSelectChange = (selectedRowKey: any[]) => {
      if (setSelectedRowKeys) {
        setSelectedRowKeys(selectedRowKey)
      }
    }
    const rowSelection = {
      selectedRowKeys,
      onChange: onSelectChange,
      ...(other.rowSelections || {}),
    }

    return (
      <Container>
        <BatchOperation
          selectedSubRowKeys={selectedSubRowKeys}
          selectedRowKeys={selectedRowKeys}
          batchOperation={batchOperation}
          setSelectedRowKeys={setSelectedRowKeys}
          setSelectedSubRowKeys={setSelectedSubRowKeys}
        />

        {isScript ? (
          <Table
            columns={resizableColumns}
            components={components}
            dataSource={dataSource}
            loading={loading}
            rowKey="id"
            getPopupContainer={() => document.body}
            {...other}
          />
        ) : (
          <Table
            scroll={{ x: 800 }}
            columns={resizableColumns}
            components={components}
            dataSource={dataSource}
            loading={loading}
            expandedRowKeys={expandedRowKeys}
            expandable={
              isNested
                ? {
                    expandedRowRender,
                    onExpand,
                    expandIcon: ({ expanded, onExpand, record }) =>
                      record?.child ? (
                        expanded ? (
                          <SvgIcon
                            name={'上拉'}
                            onClick={e => onExpand(record, e)}
                            style={{ cursor: 'pointer', width: '14px', height: '14px' }}
                          />
                        ) : (
                          <IconFont type={'icon-xiala1'} onClick={e => onExpand(record, e)} />
                        )
                      ) : null,
                  }
                : {}
            }
            // scroll={{ x: tableWidth, ...other.scroll }}
            rowKey="id"
            pagination={{
              current: searchParams?.page,
              pageSize: searchParams?.limit,
              total: total,
              showTotal: totalCount => <ShowTotal>{totalCount}</ShowTotal>,
              showSizeChanger: true,
              showQuickJumper: true,
              onChange: (page, pageSize) => {
                setSearchParams(origin => ({ ...origin, page, limit: pageSize as number }))
              },
            }}
            getPopupContainer={() => document.body}
            {...other}
            rowSelection={{ ...rowSelection, ...other.rowSelection }}
          />
        )}
      </Container>
    )
  },
)
const Container = styled.div`
  --arh-color: #999;
  .resizable-handler {
    display: flex;
    align-items: center;
    .resizable-line {
      background: var(--arh-color, #000);
    }
  }
  .resizable-line {
    width: 1px;
    height: 16px;
  }
  .react-resizable {
    width: 100% !important;
  }
  .ant-table-filter-column {
    justify-content: flex-start;
    .ant-table-column-title {
      flex: unset;
    }
    .ant-table-filter-trigger:hover {
      background: transparent;
    }
  }
  tr.ant-table-measure-row {
    visibility: collapse;
  }
`

const Icon = styled(IconFont)`
  font-size: 14px;
  color: #666;
  &:hover {
    color: #1a2b51;
  }
`
const Reset = styled.div`
  color: #999;
  cursor: pointer;
  transition: all 0.2s;
  &:hover {
    color: #1a2b51;
  }
`
